import { TRANSPORT_ATTENTION_KINDS, TRANSPORT_DISPLAY_STATUSES, TRANSPORT_REQUEST_SOURCES } from "@seagames/contracts";
import { Outlet, createFileRoute, redirect, useParams } from "@tanstack/react-router";
import { z } from "zod";
import { RequestsView } from "../../components/transport/requests-view";
import { useGamesTimeZone } from "../../components/travel-leg-form";
import { useDefaultGamesId } from "../../lib/competition/games";
import { keysetSearch, searchText } from "../../lib/list-page/list-search";
import { canDispatch } from "../../lib/transport/access";

/**
 * The Requests tab (dispatch desk). URL = the list's state in the API's own
 * keys, every param lenient (`keysetSearch`). The trip page
 * (`requests/$requestId`) nests here so its back arrow returns to the same
 * filters and page.
 */
export const Route = createFileRoute("/_app/logistics/transportation/requests")({
	validateSearch: keysetSearch({
		tab: z.enum(["upcoming", "past"]),
		date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
		status: z
			.string()
			.max(200)
			.refine((v) => v.split(",").every((s) => (TRANSPORT_DISPLAY_STATUSES as readonly string[]).includes(s))),
		source: z.enum(TRANSPORT_REQUEST_SOURCES),
		cluster_id: z.string().uuid(),
		responsible_organization_id: z.string().uuid(),
		wheelchair: z.boolean(),
		redispatch: z.boolean(),
		attention: z.enum(TRANSPORT_ATTENTION_KINDS),
		q: searchText(),
	}),
	beforeLoad: ({ context }) => {
		if (!canDispatch(context.session)) throw redirect({ to: "/logistics/transportation" });
	},
	component: RequestsShell,
});

/** The trip page nests under this route: when it matched, render it instead of the list. */
function RequestsShell() {
	const params = useParams({ strict: false }) as { requestId?: string };
	return params.requestId !== undefined ? <Outlet /> : <RequestsPage />;
}

function RequestsPage() {
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const gamesId = useDefaultGamesId(search.games_id);
	const timeZone = useGamesTimeZone(gamesId);
	if (!gamesId || !timeZone)
		return (
			<div className="grid gap-[1rem]" aria-busy>
				<div className="skeleton h-[3rem] rounded-[0.625rem]" />
				<div className="skeleton h-[20rem] rounded-[0.625rem]" />
			</div>
		);
	return <RequestsView session={session} gamesId={gamesId} timeZone={timeZone} search={search} navigate={navigate} />;
}
